import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const userRef = useRef(null);
  const initialCheckDone = useRef(false);

  // Keep userRef in sync
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Check maintenance mode periodically
  const checkMaintenanceMode = useCallback(async () => {
    try {
      const response = await api.get("/public/settings");
      const isInMaintenance = response.data?.maintenance_mode === true;
      const msg = (response.data?.maintenance_message || "").trim();
      setMaintenanceMessage(msg);
      const currentUser = userRef.current;

      if (isInMaintenance && currentUser && !["admin", "super_admin"].includes(currentUser.role)) {
        setMaintenanceMode(true);
        localStorage.removeItem("token");
        delete api.defaults.headers.common["Authorization"];
        setUser(null);
      } else {
        setMaintenanceMode(isInMaintenance);
      }
    } catch (error) {
      console.error("Maintenance check error:", error);
    }
  }, []); // No dependencies - uses ref

  // Initial auth check - runs only once
  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;
    
    const token = localStorage.getItem("token");
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Check maintenance mode every 30 seconds
  useEffect(() => {
    checkMaintenanceMode();
    const interval = setInterval(checkMaintenanceMode, 30000);
    return () => clearInterval(interval);
  }, [checkMaintenanceMode]);

  useEffect(() => {
    const refresh = () => checkMaintenanceMode();
    window.addEventListener("focus", refresh);
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [checkMaintenanceMode]);

  const fetchUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    // Check maintenance mode before registration
    try {
      const settingsRes = await api.get("/public/settings");
      if (settingsRes.data?.maintenance_mode === true) {
        throw new Error("Sistem bakımda. Kayıt işlemi yapılamaz.");
      }
    } catch (settingsError) {
      if (settingsError.message?.includes("bakımda")) {
        throw settingsError;
      }
    }
    
    const response = await api.post("/auth/register", { name, email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("rootseo_cart"); // Clear cart on logout
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      // Only update if data actually changed to prevent unnecessary re-renders
      setUser(prev => {
        const newData = response.data;
        if (JSON.stringify(prev) === JSON.stringify(newData)) {
          return prev; // Return same reference if no change
        }
        return newData;
      });
    } catch (error) {
      console.error("Refresh user error:", error);
    }
  };

  // Rozet bildirimini temizle
  const clearBadgeNotifications = async () => {
    try {
      await api.post("/auth/clear-badge-notification");
      // Kullanıcıyı güncelle
      setUser(prev => prev ? { ...prev, pending_badge_notifications: [] } : null);
    } catch (error) {
      console.error("Clear badge notification error:", error);
    }
  };

    return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      loading, 
      maintenanceMode,
      maintenanceMessage,
      login, 
      register, 
      logout, 
      fetchUser, 
      refreshUser,
      clearBadgeNotifications
    }}>
      {children}
    </AuthContext.Provider>
  );
};
